import React, { Component } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import Player from "../player/Player";
import {
    COMMAND_START_KICK_VOTE,
    COMMAND_REGISTER_KICK_VOTE,
    PARAM_TARGET,
    PARAM_VOTE,
} from "../GlobalDefinitions";
import "./KickVoteManager.css";

const COOLDOWN_SECONDS = 120;

class KickVoteManager extends Component {
    constructor(props) {
        super(props);
        this.state = {
            view: "idle",        // idle | selecting | voting
            selected: null,
            localTarget: null,
            pendingVote: false,
            cooldownUntil: 0,    // epoch ms when cooldown expires
            cooldownLeft: 0,     // seconds remaining (for display)
        };
        this.el = document.createElement("div");
        this.el.id = "kv-manager-portal";
        this._cooldownTimer = null;
    }

    componentDidMount() {
        document.body.appendChild(this.el);
    }

    componentWillUnmount() {
        document.body.removeChild(this.el);
        if (this._cooldownTimer) clearInterval(this._cooldownTimer);
    }

    componentDidUpdate(prevProps) {
        const { active, votes, username, status } = this.props;

        if (!prevProps.active && active) {
            this.setState({ view: "voting", selected: null, pendingVote: false });
        }

        if (prevProps.active && !active) {
            const wasInitiator = !!this.state.localTarget;
            const failed = (status || "").toLowerCase().includes("failed");
            this.setState({ view: "idle", localTarget: null, pendingVote: false });
            if (wasInitiator && failed) {
                this.startCooldown();
            }
        }

        const votedNow = votes && votes.hasOwnProperty(username);
        const votedBefore = prevProps.votes && prevProps.votes.hasOwnProperty(prevProps.username);
        if (!votedBefore && votedNow && this.state.pendingVote) {
            this.setState({ pendingVote: false });
        }
    }

    startCooldown() {
        const until = Date.now() + COOLDOWN_SECONDS * 1000;
        this.setState({ cooldownUntil: until, cooldownLeft: COOLDOWN_SECONDS });
        if (this._cooldownTimer) clearInterval(this._cooldownTimer);
        this._cooldownTimer = setInterval(() => {
            const left = Math.ceil((this.state.cooldownUntil - Date.now()) / 1000);
            if (left <= 0) {
                clearInterval(this._cooldownTimer);
                this._cooldownTimer = null;
                this.setState({ cooldownUntil: 0, cooldownLeft: 0 });
            } else {
                this.setState({ cooldownLeft: left });
            }
        }, 1000);
    }

    startVote() {
        const { selected } = this.state;
        if (!selected) return;
        this.props.sendWSCommand(COMMAND_START_KICK_VOTE, { [PARAM_TARGET]: selected });
        this.setState({ view: "voting", localTarget: selected, selected: null, pendingVote: false });
    }

    castVote(yes) {
        if (this.state.pendingVote) return;
        this.setState({ pendingVote: true });
        this.props.sendWSCommand(COMMAND_REGISTER_KICK_VOTE, { [PARAM_VOTE]: yes });
    }

    clearLocalVote() {
        this.setState({ view: "idle", localTarget: null, selected: null, pendingVote: false });
    }

    // ---- Renders ----

    renderFab() {
        const { active } = this.props;
        const { view, cooldownLeft } = this.state;
        const onCooldown = cooldownLeft > 0;
        const isOpen = view === "selecting";
        const disabled = active || onCooldown;

        let label = "KICK PLAYER";
        if (onCooldown) label = `KICK PLAYER (${cooldownLeft}s)`;

        return (
            <button
                className={"kvm-fab" + (isOpen ? " kvm-fab--open" : "") + (onCooldown ? " kvm-fab--cooldown" : "")}
                disabled={disabled}
                onClick={() => !disabled && this.setState({ view: isOpen ? "idle" : "selecting", selected: null })}
                title={onCooldown ? `Cooldown: ${cooldownLeft}s remaining` : active ? "Vote in progress" : "Start a kick vote"}
            >
                {label}
            </button>
        );
    }

    renderSelectorCard() {
        const others = this.props.usernames.filter(u => u !== this.props.username);
        return (
            <div className="kvm-card">
                <div className="kvm-card-title">Choose player to kick</div>
                <div className="kvm-player-grid">
                    {others.map(u => (
                        <Player
                            key={u}
                            name={u}
                            showRole={false}
                            icon={this.props.icons[u]}
                            useAsButton={true}
                            isSelected={this.state.selected === u}
                            onClick={() => this.setState({ selected: u })}
                        />
                    ))}
                </div>
                <div className="kvm-btn-row">
                    <button className="kvm-btn-cancel"
                        onClick={() => this.setState({ view: "idle", selected: null })}>
                        CANCEL
                    </button>
                    <button className="kvm-btn-confirm"
                        disabled={!this.state.selected}
                        onClick={() => this.startVote()}>
                        START VOTE
                    </button>
                </div>
            </div>
        );
    }

    renderVoteCard() {
        const { target, initiator, votes, eligibleCount, username } = this.props;
        const displayTarget = target || this.state.localTarget || "";
        const yesVotes = votes ? Object.values(votes).filter(v => v).length : 0;
        const totalVoted = votes ? Object.keys(votes).length : 0;
        const hasVoted = votes && votes.hasOwnProperty(username);
        const { pendingVote } = this.state;

        return (
            <div className="kvm-card">
                <div className="kvm-card-title">KICK VOTE</div>
                {initiator && (
                    <div className="kvm-vote-initiator">
                        Started by <span className="kvm-highlight">{initiator}</span>
                    </div>
                )}
                <div className="kvm-vote-target">
                    Remove <span className="kvm-highlight">{displayTarget}</span>?
                </div>
                <div className="kvm-vote-tally">
                    {yesVotes} YES &nbsp;·&nbsp; {totalVoted}/{eligibleCount || "?"} voted
                </div>

                {(hasVoted || pendingVote) ? (
                    <div className="kvm-vote-msg">
                        {pendingVote && !hasVoted ? "Submitting…" : "Vote recorded."}
                    </div>
                ) : (
                    <div className="kvm-vote-btn-row">
                        <button className="kvm-btn-kick" onClick={() => this.castVote(true)}>KICK</button>
                        <button className="kvm-btn-keep" onClick={() => this.castVote(false)}>KEEP</button>
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { active, target, username } = this.props;
        const { view } = this.state;

        if (active && target === username) {
            return ReactDOM.createPortal(null, this.el);
        }

        const showVote = active || view === "voting";
        const showSelector = !active && view === "selecting";

        return ReactDOM.createPortal(
            <div className="kvm-anchor">
                {showVote && this.renderVoteCard()}
                {showSelector && this.renderSelectorCard()}
                {this.renderFab()}
            </div>,
            this.el
        );
    }
}

KickVoteManager.defaultProps = {
    votes: {},
    eligibleCount: 0,
    usernames: [],
    icons: {},
    userCount: 0,
    active: false,
    target: "",
    initiator: "",
    status: "",
};

KickVoteManager.propTypes = {
    active: PropTypes.bool.isRequired,
    target: PropTypes.string,
    initiator: PropTypes.string,
    status: PropTypes.string,
    votes: PropTypes.object,
    eligibleCount: PropTypes.number,
    username: PropTypes.string.isRequired,
    usernames: PropTypes.array.isRequired,
    icons: PropTypes.object.isRequired,
    userCount: PropTypes.number.isRequired,
    sendWSCommand: PropTypes.func.isRequired,
};

export default KickVoteManager;
